import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';
import './QuickLeaguesPage.css';

interface LeagueFromDB {
  id: string;
  name: string;
  status: string;
  season_year: number;
  league_type: string;
  entry_fee: number;
  max_teams: number;
  min_teams: number;
  join_window_end: string;
  setup_window_end: string;
  tier: number;
  total_prize_pool: number;
  current_day: number;
  created_at: string;
}

const QuickLeaguesPage: React.FC = () => {
  const [leagues, setLeagues] = React.useState<LeagueFromDB[]>([]);
  const [loading, setLoading] = React.useState(true);

  const loadLeagues = async () => {
    if (!isSupabaseConfigured() || !supabase) return;
    
    const { data } = await supabase!
      .from('leagues')
      .select('*')
      .in('league_type', QL_ARRAY as readonly string[])
      .order('created_at', { ascending: false });

    if (data) setLeagues(data as LeagueFromDB[]);
    setLoading(false);
  };

  useEffect(() => { loadLeagues(); }, []);

  const getEntryFeeLabel = (tier: number) => {
    if (tier === 2) return '10,000 $BALLS ($100)';
    return '2,500 $BALLS ($25)';
  };

  const getStatusBadge = (league: LeagueFromDB) => {
    if (league.status === 'active' && league.join_window_end && new Date(league.join_window_end) > new Date()) {
      return <span className="badge badge-joining">🟢 Open</span>;
    }
    if (league.status === 'waiting') return <span className="badge badge-joining">⏳ Waiting</span>;
    if (league.status === 'drafting') return <span className="badge badge-hold">📋 Drafting</span>;
    if (league.status === 'free_agency') return <span className="badge badge-fa">💰 FA</span>;
    if (league.status === 'active') return <span className="badge badge-live">⚡ Live</span>;
    if (league.status === 'playoffs') return <span className="badge badge-playoffs">🏆 Playoffs</span>;
    if (league.status === 'off_season') return <span className="badge badge-complete">✅ Done</span>;
    return <span className="badge">{league.status}</span>;
  };

  const getTimeRemaining = (dateStr: string) => {
    const diff = new Date(dateStr).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const isOpenForJoin = (league: LeagueFromDB) => {
    return league.status === 'active' 
      && league.join_window_end 
      && new Date(league.join_window_end) > new Date();
  };

  return (
    <div className="quick-leagues-page">
      <h1>⚡ Quick Leagues</h1>
      <p className="subtitle">Claim a team, compete for $BALLS. Full season sim — auto-played.</p>

      {loading ? (
        <p>Loading leagues...</p>
      ) : leagues.length === 0 ? (
        <div className="no-leagues">
          <p>No quick leagues yet. A new one will be created shortly — check back soon!</p>
        </div>
      ) : (
        <div className="ql-grid">
          {leagues.map(league => {
            const isDrafting = league.status === 'drafting';
            const navPath = isDrafting ? `/league/${league.id}/draft-sheet` : `/league/${league.id}`;
            return (
            <div key={league.id} className="ql-card" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: navPath }))}>
              <div className="ql-card-header">
                <span className="ql-name">{league.name}</span>
                {getStatusBadge(league)}
              </div>
              <div className="ql-card-body">
                <div className="ql-info-row">
                  <span>💰 Entry</span>
                  <span>{getEntryFeeLabel(league.tier)}</span>
                </div>
                <div className="ql-info-row">
                  <span>👥 Teams</span>
                  <span>{league.max_teams || 12}</span>
                </div>
                {isDrafting && (
                  <div className="ql-info-row highlight" style={{color: '#8b5cf6'}}>
                    <span>📋 Draft</span>
                    <span>In progress — Click to set priorities!</span>
                  </div>
                )}
                {isOpenForJoin(league) && (
                  <div className="ql-info-row highlight">
                    <span>⏰ Join window</span>
                    <span>{getTimeRemaining(league.join_window_end)}</span>
                  </div>
                )}
                {(league.status === 'active' || league.status === 'playoffs') && league.current_day > 1 && (
                  <div className="ql-info-row">
                    <span>📅 Day</span>
                    <span>{league.current_day}</span>
                  </div>
                )}
              </div>
              <div className="ql-card-footer">
                <span className="ql-type">{league.league_type}</span>
                <span className="ql-view">View League →</span>
              </div>
            </div>
            )
          })}
        </div>
      )}
    </div>
  );
};

export default QuickLeaguesPage;
